import PratusTextArea from 'components/DesignSystems/New/PratusTextArea';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import AssignedResourcesTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/AssignedResourcesTable';
import CommandPrioritiesTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/CommandPrioritiesTable';
import CriticalResourceRequestsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/CriticalResourceRequestsTable';
import IncidentTypeCheckboxGroup from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/IncidentTypeCheckboxGroup';
import NotableItemsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/NotableItemsTable';
import PlannedActionsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/PlannedActionsTable';
import ReportVersionToggle from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/ReportVersionToggle';
import ResponderDeathsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/ResponderDeathsTable';
import ResponderInjuriesTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/ResponderInjuriesTable';
import { Row } from 'react-bootstrap';
import { IncidentStatusSummaryFormFields } from 'types/iapBaseTypes';

export const SituationSummaryField = ({
  statusSummaryId,
  originalSituationSummary,
  readOnly,
}: {
  statusSummaryId?: string;
  originalSituationSummary?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Situation Summary"
        disabled={readOnly}
        onSave={(value) =>
          upsertIncidentStatusSummary({ situationSummary: value })
        }
        defaultValue={originalSituationSummary}
      />
    </Row>
  );
};

export const LifeSafetyHealthIssuesField = ({
  statusSummaryId,
  originalLifeSafetyHealthIssues,
  readOnly,
}: {
  statusSummaryId?: string;
  originalLifeSafetyHealthIssues?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Life, Safety, and Health Issues"
        disabled={readOnly}
        onSave={(value) =>
          upsertIncidentStatusSummary({ lifeSafetyHealthIssues: value })
        }
        defaultValue={originalLifeSafetyHealthIssues}
      />
    </Row>
  );
};

export const MarineTransportationSystemField = ({
  statusSummaryId,
  originalMarineTransportationSystem,
  readOnly,
}: {
  statusSummaryId?: string;
  originalMarineTransportationSystem?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Impacts to Marine Transportation System and Critical Infrastructure"
        disabled={readOnly}
        onSave={(value) =>
          upsertIncidentStatusSummary({ marineTransportationSystem: value })
        }
        defaultValue={originalMarineTransportationSystem}
        maxLength={1000}
      />
    </Row>
  );
};

export const AmplifyingInformationField = ({
  statusSummaryId,
  originalAmplifyingInformation,
  readOnly,
}: {
  statusSummaryId?: string;
  originalAmplifyingInformation?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Amplifying Information"
        disabled={readOnly}
        onSave={(value) =>
          upsertIncidentStatusSummary({ amplifyingInformation: value })
        }
        defaultValue={originalAmplifyingInformation}
        maxLength={1000}
      />
    </Row>
  );
};

export const CommentsField = ({
  statusSummaryId,
  originalComments,
  readOnly,
}: {
  statusSummaryId?: string;
  originalComments?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Comments"
        disabled={readOnly}
        onSave={(value) => upsertIncidentStatusSummary({ comments: value })}
        defaultValue={originalComments}
        maxLength={1000}
      />
    </Row>
  );
};

export const MediaPhotosPublicOutreachField = ({
  statusSummaryId,
  originalMediaPhotosPublicOutreach,
  readOnly,
}: {
  statusSummaryId?: string;
  originalMediaPhotosPublicOutreach?: string;
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Media, Photos, and Public Outreach"
        disabled={readOnly}
        onSave={(value) =>
          upsertIncidentStatusSummary({ mediaPhotosPublicOutreach: value })
        }
        defaultValue={originalMediaPhotosPublicOutreach}
        maxLength={1000}
      />
    </Row>
  );
};

const IncidentStatusSummaryFormComponents = ({
  statusSummaryId,
  originalIncidentStatusSummary,
  readOnly = false,
}: {
  statusSummaryId: string;
  originalIncidentStatusSummary: IncidentStatusSummaryFormFields;
  readOnly?: boolean;
}) => (
  <>
    <ReportVersionToggle
      statusSummaryId={statusSummaryId}
      originalReportVersion={originalIncidentStatusSummary?.reportVersion}
      readOnly={readOnly}
    />
    <IncidentTypeCheckboxGroup
      statusSummaryId={statusSummaryId}
      originalIncidentType={originalIncidentStatusSummary?.incidentType}
      originalOtherIncidentType={
        originalIncidentStatusSummary?.otherIncidentType
      }
      readOnly={readOnly}
    />
    <SituationSummaryField
      statusSummaryId={statusSummaryId}
      originalSituationSummary={originalIncidentStatusSummary?.situationSummary}
      readOnly={readOnly}
    />
    <CommandPrioritiesTable
      statusSummaryId={statusSummaryId}
      originalCommandPriorities={
        originalIncidentStatusSummary?.commandPriorities
      }
      readOnly={readOnly}
    />
    <NotableItemsTable
      statusSummaryId={statusSummaryId}
      originalNotableItems={originalIncidentStatusSummary?.notableItems}
      readOnly={readOnly}
    />
    <ResponderInjuriesTable
      statusSummaryId={statusSummaryId}
      originalResponderInjuries={
        originalIncidentStatusSummary?.responderInjuries
      }
      readOnly={readOnly}
    />
    <ResponderDeathsTable
      statusSummaryId={statusSummaryId}
      originalResponderDeaths={originalIncidentStatusSummary?.responderDeaths}
      readOnly={readOnly}
    />
    <LifeSafetyHealthIssuesField
      statusSummaryId={statusSummaryId}
      originalLifeSafetyHealthIssues={
        originalIncidentStatusSummary?.lifeSafetyHealthIssues
      }
      readOnly={readOnly}
    />
    <MarineTransportationSystemField
      statusSummaryId={statusSummaryId}
      originalMarineTransportationSystem={
        originalIncidentStatusSummary?.marineTransportationSystem
      }
      readOnly={readOnly}
    />
    <CriticalResourceRequestsTable
      statusSummaryId={statusSummaryId}
      originalCriticalResourceRequests={
        originalIncidentStatusSummary?.criticalResourceRequests
      }
      readOnly={readOnly}
    />
    <PlannedActionsTable
      statusSummaryId={statusSummaryId}
      originalPlannedActions={originalIncidentStatusSummary?.plannedActions}
      readOnly={readOnly}
    />
    <AssignedResourcesTable
      statusSummaryId={statusSummaryId}
      originalAssignedResources={
        originalIncidentStatusSummary?.assignedResources
      }
      readOnly={readOnly}
    />
    <AmplifyingInformationField
      statusSummaryId={statusSummaryId}
      originalAmplifyingInformation={
        originalIncidentStatusSummary?.amplifyingInformation
      }
      readOnly={readOnly}
    />
    <CommentsField
      statusSummaryId={statusSummaryId}
      originalComments={originalIncidentStatusSummary?.comments}
      readOnly={readOnly}
    />
    <MediaPhotosPublicOutreachField
      statusSummaryId={statusSummaryId}
      originalMediaPhotosPublicOutreach={
        originalIncidentStatusSummary?.mediaPhotosPublicOutreach
      }
      readOnly={readOnly}
    />
  </>
);

export default IncidentStatusSummaryFormComponents;
